import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { organizationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { formatCurrency } from "@/lib/currency";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Globe, Loader2, Save, Check } from "lucide-react";

export default function OrganizationSettingsPage() {
  const { hasPermission, refreshAuth } = useAuth();
  const canEdit = hasPermission("TENANT.CONFIGURE");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [selected, setSelected] = useState("INR");
  const [options, setOptions] = useState([]);
  const [scoped, setScoped] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationService.regionalSettings();
      setCurrency(data.currency);
      setSelected(data.currency);
      setOptions(data.supportedCurrencies || []);
      setScoped(data.scoped);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await organizationService.setCurrency(selected);
      setCurrency(selected);
      toast.success("Currency updated successfully");
      await refreshAuth();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const dirty = selected !== currency;

  return (
    <div data-testid="organization-settings-page">
      <PageHeader
        title="Organization Settings"
        subtitle="Regional Settings — configure how your organization displays money across the platform"
      />

      {loading ? (
        <div className="flex justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="max-w-2xl">
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden" data-testid="regional-settings-card">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
              <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Globe className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-semibold text-foreground">Regional Settings</h3>
                <p className="text-xs text-muted-foreground">Currency applies to all monetary values for your tenant.</p>
              </div>
            </div>

            <div className="px-6 py-6 space-y-5">
              {!scoped && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-sm px-4 py-3" data-testid="org-settings-no-tenant">
                  This account is not bound to a tenant. Currency is configured per tenant; sign in as a tenant administrator to change it.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Display Currency</label>
                <Select value={selected} onValueChange={setSelected} disabled={!canEdit || !scoped}>
                  <SelectTrigger className="w-full max-w-sm" data-testid="currency-select-trigger">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="font-mono w-7 text-muted-foreground">{c.symbol}</span>
                          <span className="font-semibold">{c.code}</span>
                          <span className="text-muted-foreground">— {c.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Default for new tenants is <span className="font-semibold">INR ({"\u20b9"})</span>.
                </p>
              </div>

              <div className="rounded-lg bg-muted/50 border border-border px-4 py-3" data-testid="currency-preview">
                <p className="text-xs text-muted-foreground mb-1">Preview</p>
                <p className="text-lg font-bold text-foreground">{formatCurrency(1250000, selected)}</p>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={save}
                  disabled={!canEdit || !scoped || !dirty || saving}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="save-currency-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Changes
                </button>
                {!dirty && !saving && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid="currency-saved-indicator">
                    <Check className="h-3.5 w-3.5" /> Current: {currency}
                  </span>
                )}
                {!canEdit && scoped && (
                  <span className="text-xs text-muted-foreground">You don't have permission to change this setting.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
